import { useState } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { AlertCircle, Settings, Key } from "lucide-react";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { Button } from "@/modern/components/ui/button";
import { OpenAIKeyModal } from "../components/OpenAIKeyModal";
import { useBoardPermissions } from "../hooks/useBoardPermissions";

const OpenAIDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Subscribe to board preferences changes to trigger rerender if open api key flag changes
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      return record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey;
    }
  });

  // @ts-expect-error - userPreferences is dynamically set in bryntumContext
  const hasOpenAIKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";

  // Use shared permission hook
  const { canWrite } = useBoardPermissions();

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      {!hasOpenAIKey && (
        <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-foreground mb-1">OpenAI API Key Required</h4>
                <p className="text-sm text-foreground">
                  To configure AI Prompt settings, you need to set up your OpenAI API key first.
                  {canWrite ? " Click the button to configure your API key." : " Only the Board Owner can configure the API key."}
                </p>
              </div>
            </div>
            {canWrite && (
              <Button onClick={() => setIsModalOpen(true)} size="sm" variant="default" className="flex-shrink-0">
                <Settings className="h-4 w-4 mr-2" />
                Configure Key
              </Button>
            )}
          </div>
        </div>
      )}

      {hasOpenAIKey && (
        <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              <Key className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-foreground mb-1">OpenAI API Key Configured</h4>
                <p className="text-sm text-foreground">Your OpenAI API key is configured and ready to use.</p>
              </div>
            </div>
            {canWrite && (
              <Button onClick={() => setIsModalOpen(true)} size="sm" variant="default" className="flex-shrink-0">
                <Settings className="h-4 w-4 mr-2" />
                Manage Key
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Information Section */}
      <div className="mt-6 p-4 bg-card rounded-lg border border-border">
        <h4 className="text-sm font-semibold text-foreground mb-2">About OpenAI Integration</h4>
        <p className="text-sm text-muted-foreground leading-relaxed mb-3">The OpenAI API key enables AI-powered features such as:</p>
        <ul className="text-sm text-muted-foreground space-y-1 ml-4">
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>AI-generated image inspection and analysis</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>Intelligent context-based recommendations</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>Automated content generation based on your custom prompts</span>
          </li>
        </ul>
        <p className="text-xs text-muted-foreground mt-4">
          <strong>Security Note:</strong> Your API key is stored securely and can only be viewed and managed by the Board Owner.
        </p>
      </div>

      {/* OpenAI API Key Configuration Modal */}
      <OpenAIKeyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default OpenAIDetail;
